/**
 * TransactionsWidget — „Ostatnie transakcje": faktury sprzedaży (wpływ, +) i zakupu (wydatek, −)
 * ze statusem (Zaksięgowano / Opłacono / Oczekuje / Szkic / Odrzucono) wg Figmy „08.6".
 * Klik w wiersz otwiera dokument. W trybie „Wszystkie firmy" pod tytułem widać nazwę firmy.
 */

import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowDownLeft, ArrowUpRight, Clock, FileText, type LucideIcon } from 'lucide-react';

import { Badge } from '@/components/Badge';
import { useActiveCompanyScope } from '@/lib/use-active-company';
import { cn } from '@/lib/utils';

import {
  classifyDataError,
  fetchTransactions,
  type TransactionRow,
  type TransactionStatus,
} from '../dashboard-api';
import type { WidgetRenderProps } from '../types';
import { formatMoney, type Tone } from '../widget-ui';

import { WidgetDataError, WidgetEmpty, WidgetLoading } from './WidgetState';

const STATUS_TONE: Record<TransactionStatus, 'success' | 'neutral' | 'warning' | 'danger'> = {
  booked: 'success',
  paid: 'neutral',
  pending: 'warning',
  draft: 'neutral',
  rejected: 'danger',
};

interface RowVisual {
  icon: LucideIcon;
  tone: Tone;
}

/** Ikona wiersza: wpływ zaksięgowany/opłacony → zielona strzałka, oczekujące → zegar, wydatek → strzałka w górę. */
export function transactionVisual(row: Pick<TransactionRow, 'direction' | 'status'>): RowVisual {
  if (row.status === 'draft') return { icon: FileText, tone: 'neutral' };
  if (row.status === 'pending') return { icon: Clock, tone: 'warning' };
  if (row.direction === 'in') return { icon: ArrowDownLeft, tone: 'success' };
  return { icon: ArrowUpRight, tone: 'neutral' };
}

const TILE_CLASS: Record<string, string> = {
  success: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  warning: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  neutral: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
};

export function TransactionsWidget({ widget }: WidgetRenderProps) {
  const { t } = useTranslation();
  const { isAll, mode, companyId } = useActiveCompanyScope();
  const limit = widget.type === 'transactions' ? (widget.config.limit ?? 5) : 5;
  const blocked = mode === 'none';

  const query = useQuery({
    queryKey: ['tenant', 'dashboard', 'transactions', limit, companyId ?? 'all'],
    queryFn: () => fetchTransactions(limit, isAll ? 'all' : 'company'),
    enabled: widget.type === 'transactions' && !blocked,
    staleTime: 30_000,
    retry: false,
  });

  if (widget.type !== 'transactions') return null;

  if (blocked) return <WidgetEmpty message={t('tenant.dashboard.pickCompany')} />;
  if (query.isLoading) return <WidgetLoading />;
  if (query.isError)
    return (
      <WidgetDataError kind={classifyDataError(query.error)} onRetry={() => query.refetch()} />
    );

  const rows = query.data ?? [];
  if (rows.length === 0)
    return <WidgetEmpty message={t('tenant.dashboard.widgets.transactions.empty')} />;

  return (
    <ul className="flex flex-col divide-y divide-border-secondary">
      {rows.map((row) => {
        const visual = transactionVisual(row);
        const Icon = visual.icon;
        const title = row.number ?? row.title ?? '—';
        const meta = [
          isAll ? row.companyName : null,
          row.counterparty,
          new Date(row.date).toLocaleDateString('pl-PL'),
        ]
          .filter(Boolean)
          .join(' · ');
        const amount = row.amountGross;
        const positive = amount !== null && amount > 0;
        const content = (
          <>
            <span
              aria-hidden="true"
              className={cn(
                'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
                TILE_CLASS[visual.tone] ?? TILE_CLASS.neutral,
              )}
            >
              <Icon className="h-4 w-4" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="truncate text-[13px] font-medium text-text-primary" title={title}>
                {title}
              </div>
              <div className="truncate text-[11px] text-text-tertiary" title={meta}>
                {meta}
              </div>
            </div>
            <div className="flex shrink-0 flex-col items-end gap-1">
              <span
                className={cn(
                  'text-[13px] font-semibold tabular-nums',
                  positive ? 'text-green-600 dark:text-green-400' : 'text-text-primary',
                )}
              >
                {amount === null
                  ? '—'
                  : `${positive ? '+' : ''}${formatMoney(amount, row.currency ?? 'PLN')}`}
              </span>
              <Badge tone={STATUS_TONE[row.status]} size="sm">
                {t(`tenant.dashboard.widgets.transactions.status.${row.status}`)}
              </Badge>
            </div>
          </>
        );
        return (
          <li key={row.id} className="py-2 first:pt-0 last:pb-0">
            {row.href ? (
              <Link
                to={row.href}
                onMouseDown={(e) => e.stopPropagation()}
                className="flex items-center gap-3 rounded-lg hover:bg-bg-secondary"
              >
                {content}
              </Link>
            ) : (
              <div className="flex items-center gap-3">{content}</div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
